import React, { useEffect, useMemo, useState, JSX } from 'react';
import { ApiItinerary } from '@ibe/api';
import { Carousel, CarouselControl, CarouselItem } from 'reactstrap';
import fallback from '../../../_Translations/generated/package-details.en.t.json';
import Keys from '../../../_Translations/generated/package-details.en.t.json.keys';
import useTranslation from '../../Hooks/useTranslation';
import CarouselRow from './CarouselRow';

interface Props {
  itineraries?: ApiItinerary[];
  activeDayId: number;
  setActiveDayId: (activeDayId: number) => void;
  isSmall: boolean;
}

function divideIntoChunks(items: Array<ApiItinerary>, chunkSize: number): ApiItinerary[][] {
  const chunks = [];

  // divide array into smaller pieces
  while (items.length) {
    chunks.push(items.splice(0, chunkSize));
  }
  return chunks;
}

const CarouselComponent = (props: Props): JSX.Element => {
  const { itineraries, activeDayId, setActiveDayId, isSmall } = props;
  const { t } = useTranslation('package-details', fallback, Keys);

  const [activeIndex, setActiveIndex] = useState(0);
  const [animating, setAnimating] = useState(false);

  let items: ApiItinerary[][] = useMemo(() => {
    const data = itineraries && itineraries.slice();
    if (!data) return [];
    if (isSmall) {
      items = divideIntoChunks(data, 1);
    } else {
      items = divideIntoChunks(data, 3);
    }
    return items;
  }, [itineraries, isSmall]);

  const next = (): void => {
    if (animating) return;
    const nextIndex = activeIndex === items.length - 1 ? 0 : activeIndex + 1;
    setActiveIndex(nextIndex);
    if (isSmall) {
      setActiveDayId(nextIndex);
    }
  };

  const previous = (): void => {
    if (animating) return;
    const nextIndex = activeIndex === 0 ? items.length - 1 : activeIndex - 1;
    setActiveIndex(nextIndex);
    if (isSmall) {
      setActiveDayId(nextIndex);
    }
  };

  const slides = useMemo(() => {
    return items.map((slidesItem: ApiItinerary[]): JSX.Element | false => {
      return (
        items.length > 0 && (
          <CarouselItem
            onExiting={(): void => setAnimating(true)}
            onExited={(): void => setAnimating(false)}
            key={`${slidesItem[0].title}${slidesItem[0].numberOfDay}`}
          >
            {slidesItem && (
              <div>
                <CarouselRow
                  chunk={slidesItem}
                  renderCarouselRowIsSmall={isSmall}
                  itineraries={itineraries}
                  activeDayId={activeDayId}
                  setActiveDayId={setActiveDayId}
                />
              </div>
            )}
          </CarouselItem>
        )
      );
    });
  }, [items, setAnimating, isSmall, activeDayId, itineraries, setActiveDayId]);

  useEffect(() => {
    if (isSmall) {
      setActiveIndex(activeDayId);
    }

    if (!isSmall) {
      if (activeIndex > items.length) {
        setActiveIndex(Math.floor(activeIndex / 3));
      }
    }
  }, [isSmall, setActiveIndex, activeIndex, items, activeDayId]);

  return (
    items && (
      <div className="w-100">
        <Carousel
          className="w-100"
          activeIndex={activeIndex}
          next={next}
          previous={previous}
          interval={false}
        >
          {slides}
          <CarouselControl
            className="mr-1"
            direction="prev"
            directionText={t(Keys.prev)}
            onClickHandler={previous}
          />
          <CarouselControl
            className="ml-1"
            direction="next"
            directionText={t(Keys.next)}
            onClickHandler={next}
          />
        </Carousel>
      </div>
    )
  );
};

export default CarouselComponent;
